import { Injectable } from '@angular/core';
import { FlamingoHttpService } from '@flamingo/service/flamingo-http.service';
import { WorkflowYear } from '@x/model/workflow-year.model';
import { BehaviorSubject, Observable } from 'rxjs';
import { filter, map, tap } from 'rxjs/operators';

@Injectable({
  providedIn: 'root',
})
export class GlobalWorkflowYearConfigurationService {
  private _years$ = new BehaviorSubject<WorkflowYear[] | null>(null);
  years$ = this._years$.asObservable().pipe(filter((res): res is WorkflowYear[] => res !== null));

  constructor(private http: FlamingoHttpService) {}

  get years(): WorkflowYear[] {
    return this._years$.value!;
  }

  set years(years: WorkflowYear[]) {
    this._years$.next(years);
  }

  getWorkflowYears(): Observable<WorkflowYear[]> {
    return this.http.get<WorkflowYear[]>(`/workflow-year/configurations`).pipe(
      map(res => res.sort((a, b) => b.year.localeCompare(a.year) || (a.productTypeId ?? 0) - (b.productTypeId ?? 0))),
      tap(res => (this.years = res)),
    );
  }

  /**
   * Configurations bound to a product type, grouped by product type id, years sorted desc.
   * Legacy rows (productTypeId == null) are excluded: they are not searchable, since
   * the subscription search endpoints require productTypeId.
   */
  yearsByProductType(): Map<number, string[]> {
    const grouped = new Map<number, string[]>();
    (this.years ?? [])
      .filter(config => config.productTypeId != undefined)
      .forEach(config => {
        const years = grouped.get(config.productTypeId!) ?? [];
        if (!years.includes(config.year)) {
          years.push(config.year);
        }
        grouped.set(config.productTypeId!, years);
      });
    grouped.forEach(years => years.sort((a, b) => b.localeCompare(a)));
    return grouped;
  }

  productTypeIds(): number[] {
    return [...this.yearsByProductType().keys()];
  }

  yearsFor(productTypeId: number): string[] {
    return this.yearsByProductType().get(productTypeId) ?? [];
  }

  latestYearFor(productTypeId: number): string | undefined {
    return this.yearsFor(productTypeId)[0];
  }
}
